// Exported so other surfaces (e.g. AdventureSitesMap's detail panel) can
// reuse this site's one real, already-published helpline data instead of
// duplicating or inventing their own numbers.
export const HELPLINE_LABEL = "Call Tourist Facilitation Helpline 1424";
export const HELPLINE_HREF = "tel:1424";
export const HELPLINE_DISPLAY = "1424";
export const UAN_LABEL = "Call UAN 051-111-148-148";
export const UAN_HREF = "tel:051111148148";
export const UAN_DISPLAY = "051-111-148-148";

// 1424 is a domestic short code — short codes never route from outside
// Pakistan, so international visitors (detected server-side via
// isInternationalVisitor()) see this dialable +92 number instead.
export const INTL_HELPLINE_LABEL = "Call Tourist Helpline +92 51 8911005";
export const INTL_HELPLINE_HREF = "tel:+92518911005";
export const INTL_HELPLINE_DISPLAY = "+92 51 8911005";

const PILL_BASE =
  "shrink-0 items-center rounded-full border border-[color-mix(in_srgb,var(--accent)_45%,transparent)] bg-[color-mix(in_srgb,var(--accent)_10%,transparent)] text-[var(--accent)] no-underline transition-colors duration-150 hover:border-[var(--accent)] hover:bg-[color-mix(in_srgb,var(--accent)_18%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]";

// Distinct from PILL_BASE: this variant renders inside SiteNav, which is
// permanently dark navy regardless of theme, so it needs fixed chrome
// colors — --accent would be invisible against that same navy in light mode.
const PILL_BASE_CHROME =
  "shrink-0 items-center rounded-full border border-[color-mix(in_srgb,var(--cta)_45%,transparent)] bg-[color-mix(in_srgb,var(--cta)_10%,transparent)] text-[var(--cta)] no-underline transition-colors duration-150 hover:border-[var(--cta)] hover:bg-[color-mix(in_srgb,var(--cta)_18%,transparent)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--cta)]";

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

/** Tablet + desktop pills for the WeatherBar: UAN + Tourist Helpline.
 *  Hidden below `sm` — mobile gets the compact variants in SiteNav instead. */
export function HelplineBarButton({ isInternational = false }: { isInternational?: boolean }) {
  return (
    <span className="hidden sm:inline-flex items-center gap-2">
      <a
        href={UAN_HREF}
        aria-label={UAN_LABEL}
        className={`inline-flex gap-1.5 px-2.5 py-1 text-[11px] font-semibold tracking-wide ${PILL_BASE}`}
      >
        <PhoneIcon className="h-3 w-3" />
        {/* Label only, at every breakpoint — the number lives in the tel: href
            (and in aria-label, so screen readers still announce it). Desktop
            used to spell out "UAN 051-111-148-148", which crowded the bar. */}
        <span>UAN</span>
      </a>
      {isInternational ? (
        <a
          href={INTL_HELPLINE_HREF}
          aria-label={INTL_HELPLINE_LABEL}
          className={`inline-flex gap-1.5 px-2.5 py-1 text-[11px] font-semibold tracking-wide ${PILL_BASE}`}
        >
          <PhoneIcon className="h-3 w-3" />
          <span className="lg:hidden">Helpline</span>
          <span className="hidden lg:inline">Tourist Helpline {INTL_HELPLINE_DISPLAY}</span>
        </a>
      ) : (
        <a
          href="tel:1424"
          aria-label={HELPLINE_LABEL}
          className={`inline-flex gap-1.5 px-2.5 py-1 text-[11px] font-semibold tracking-wide ${PILL_BASE}`}
        >
          <PhoneIcon className="h-3 w-3" />
          <span className="lg:hidden">Call 1424</span>
          <span className="hidden lg:inline">Tourist Helpline 1424</span>
        </a>
      )}
    </span>
  );
}

/** Mobile-only compact variants for the SiteNav header actions row. */
export function HelplineCompactButton({ isInternational = false }: { isInternational?: boolean }) {
  return (
    <span className="sm:hidden inline-flex items-center gap-1">
      <a
        href={UAN_HREF}
        aria-label={UAN_LABEL}
        className={`inline-flex h-10 gap-1 px-2 ${PILL_BASE_CHROME}`}
      >
        <PhoneIcon className="h-4 w-4" />
        <span className="text-[11px] font-bold tracking-wide">UAN</span>
      </a>
      {isInternational ? (
        <a
          href={INTL_HELPLINE_HREF}
          aria-label={INTL_HELPLINE_LABEL}
          className={`inline-flex h-10 gap-1 px-2 ${PILL_BASE_CHROME}`}
        >
          <PhoneIcon className="h-4 w-4" />
          {/* "Help" not "Helpline" — on narrow phones (~360px and below) the
              extra width pushed the row past its own box and clipped the
              hamburger button clean off-screen. aria-label still carries the
              full "Call Tourist Helpline +92 51 8911005" for screen readers. */}
          <span className="text-[11px] font-bold tracking-wide">Help</span>
        </a>
      ) : (
        <a
          href="tel:1424"
          aria-label={HELPLINE_LABEL}
          className={`inline-flex h-10 gap-1 px-2 ${PILL_BASE_CHROME}`}
        >
          <PhoneIcon className="h-4 w-4" />
          <span className="text-[12px] font-bold tracking-wide">1424</span>
        </a>
      )}
    </span>
  );
}
